import { Link, NavLink } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { useState } from 'react'

function NavItem({ to, children, mobile = false }: { to: string; children: React.ReactNode; mobile?: boolean }) {
  const baseClasses = mobile
    ? 'block px-5 py-4 text-lg font-medium hover:bg-carrot-50 rounded-2xl transition-all duration-300 ease-apple'
    : 'px-5 py-2 font-medium hover:text-carrot-600 transition-all duration-300 ease-apple relative group'

  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `${baseClasses} ${
          isActive
            ? mobile
              ? 'bg-carrot-100 text-carrot-700'
              : 'text-carrot-600'
            : mobile
            ? 'text-ink-700'
            : 'text-ink-700'
        }`
      }
    >
      {children}
      {!mobile && (
        <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-carrot-500 to-carrot-600 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-apple origin-left rounded-full" />
      )}
    </NavLink>
  )
}

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-ink-100/50 shadow-subtle">
      <div className="container">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
              <img
                src="/AdKarrot_white_color_transparent.png"
                alt="AdKarrot Logo"
                className="h-12 w-12 transition-all duration-300 ease-apple group-hover:scale-110 group-hover:rotate-3"
              />
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-xl text-ink-900 leading-none tracking-tight">AdKarrot</span>
              <span className="text-xs text-ink-500 leading-none mt-0.5 tracking-wider">SMART ADS, REAL RESULTS</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-2">
            <NavItem to="/services">Services</NavItem>
            <NavItem to="/case-studies">Case Studies</NavItem>
            <NavItem to="/pricing">Pricing</NavItem>
            <NavItem to="/about">About</NavItem>
            <NavItem to="/contact">Contact</NavItem>
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link
              to="/pricing"
              className="inline-flex items-center px-7 py-3.5 bg-carrot-500 hover:bg-carrot-600 text-white font-semibold rounded-2xl shadow-subtle hover:shadow-soft transition-all duration-300 ease-apple transform hover:scale-[1.02]"
            >
              Book a Call
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-3 text-ink-700 hover:bg-ink-50 rounded-2xl transition-all duration-300 ease-apple"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-ink-100/50 bg-white/95 backdrop-blur-xl">
          <nav className="container py-8 space-y-2">
            <NavItem to="/services" mobile>
              Services
            </NavItem>
            <NavItem to="/case-studies" mobile>
              Case Studies
            </NavItem>
            <NavItem to="/pricing" mobile>
              Pricing
            </NavItem>
            <NavItem to="/about" mobile>
              About
            </NavItem>
            <NavItem to="/contact" mobile>
              Contact
            </NavItem>
            <div className="pt-6">
              <Link
                to="/pricing"
                className="block text-center px-6 py-4 bg-carrot-500 hover:bg-carrot-600 text-white font-semibold rounded-2xl shadow-soft transition-all duration-300 ease-apple"
                onClick={() => setMobileMenuOpen(false)}
              >
                Book a Strategy Call
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  )
}
